import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, CircularProgress, Typography } from '@mui/material';
import { searchMedicines } from '../../api/inventoryMaster';
import { InvTextField } from '../InvField';
import {
  MEDICINE_SEARCH_DEBOUNCE_MS,
  MEDICINE_SEARCH_MIN_LENGTH,
  medicineOptionKey,
  medicineOptionLabel,
} from '../../utils/medicineSearch';

/**
 * Debounced Item Name typeahead against GET /search_medicines/?name=.
 * Free-solo so a name not in the formulary can still be entered.
 * Search runs only while the popup is open (no fetch on edit hydrate / after select).
 */
function MedicineNameAutocomplete({
  value,
  onInputChange,
  onSelect,
  error,
  disabled = false,
  label = 'Item Name *',
  inputRef,
  onFocus,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [open, setOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(() => (value || '').trim());
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch((value || '').trim()),
      MEDICINE_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;

    const liveQuery = (value || '').trim();
    if (liveQuery.length >= MEDICINE_SEARCH_MIN_LENGTH && liveQuery !== debouncedSearch) {
      setOptions([]);
      return undefined;
    }

    const query = debouncedSearch;
    if (query.length < MEDICINE_SEARCH_MIN_LENGTH) {
      setOptions([]);
      setFetchError(null);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    async function fetchOptions() {
      setLoading(true);
      setFetchError(null);
      try {
        const rows = await searchMedicines(query);
        if (cancelled || requestId !== requestIdRef.current) return;
        setOptions(rows);
      } catch (err) {
        if (cancelled || requestId !== requestIdRef.current) return;
        setFetchError(err?.message || 'Failed to search medicines');
        setOptions([]);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) setLoading(false);
      }
    }

    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, open, value]);

  const liveQuery = (value || '').trim();
  const pendingSearch = liveQuery.length >= MEDICINE_SEARCH_MIN_LENGTH && liveQuery !== debouncedSearch;
  const noOptionsText = useMemo(() => {
    if (loading || pendingSearch) return 'Searching…';
    if (fetchError) return fetchError;
    if (liveQuery.length < MEDICINE_SEARCH_MIN_LENGTH) {
      return `Type at least ${MEDICINE_SEARCH_MIN_LENGTH} characters`;
    }
    return 'No medicines found';
  }, [loading, pendingSearch, fetchError, liveQuery]);

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      blurOnSelect
      clearOnBlur={false}
      forcePopupIcon
      openOnFocus
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading || pendingSearch}
      inputValue={value || ''}
      getOptionLabel={medicineOptionLabel}
      isOptionEqualToValue={(a, b) => medicineOptionKey(a) === medicineOptionKey(b)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      ListboxProps={{ 'aria-label': 'Medicine search results' }}
      onInputChange={(_, next, reason) => {
        if (reason === 'reset') return;
        if (reason === 'input') setOpen(true);
        onInputChange(next);
      }}
      onChange={(_, next) => {
        if (next && typeof next === 'object') {
          onSelect(next);
          return;
        }
        onInputChange(typeof next === 'string' ? next : '');
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={medicineOptionKey(option)}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{option.term}</Typography>
            {(option.generic_name || option.manufacturer_name) && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {[option.generic_name, option.manufacturer_name].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <InvTextField
          {...params}
          label={label}
          error={error}
          inputRef={inputRef}
          onFocus={onFocus}
          placeholder="Search medicine…"
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Item name',
            autoComplete: 'off',
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || pendingSearch ? (
                  <CircularProgress color="inherit" size={16} aria-label="Searching medicines" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default memo(MedicineNameAutocomplete);
